import React, { useMemo, useRef, useState } from 'react';
import {
  OPS, SOURCES, SUBJECTS, NUMERIC_OPS,
  conditionValues, copyRulesTo, describeRule, describeSource,
  newCondition, newRule, positionChoices, sourceKind,
} from './setupRuleHelpers';

/**
 * The setup-time rules for one work center.
 *
 * One list, read BOTTOM-UP: the first rule that fits wins. The fallback is
 * pinned at the top and never moves; new rules are added at the bottom, where
 * they beat everything above them. That is the whole trick — an exception is
 * just a line further down, so nobody has to think about priority.
 *
 * Conditions inside a rule are joined by "and"; the values inside one condition
 * are joined by "or". There is no nesting and no explicit OR, which is what
 * keeps a rule readable as a sentence and keeps it inside what both engines can
 * evaluate.
 */
const TEAL = '#16a085';
const AMBER = '#e67e22';
const RED = '#c0392b';

const box = {
  padding: '4px 8px', fontSize: '11px', border: '1px solid #ddd',
  borderRadius: '4px', backgroundColor: 'white',
};

export default function SetupRulesSection({
  wcId,
  rules,
  onChange,
  disabled = false,
  knownFields = [],
  knownValues = {},
  hasMsaBlock = false,
  msaSettings = null,
  otherWorkCenters = [],
  onCopyTo,
  issues = [],
}) {
  const [openIndex, setOpenIndex] = useState(null);
  const [copyTarget, setCopyTarget] = useState('');
  const [copyMode, setCopyMode] = useState('append');
  const [copyNotes, setCopyNotes] = useState(null);
  const fileRef = useRef(null);

  const list = useMemo(() => rules || [], [rules]);
  // Nothing is checked here. The table is validated when Apply Changes is
  // pressed and the problems come back in `issues`, so a half-typed rule is
  // never flagged while it is being typed.
  const issuesFor = i => issues.filter(x => x.index === i);

  const replace = next => onChange(next);
  const update = (i, patch) => replace(list.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const updateCondition = (i, ci, patch) => update(i, {
    when: list[i].when.map((c, n) => (n === ci ? { ...c, ...patch } : c)),
  });

  const addRule = () => {
    // 80L, 90L and HTE have no rules at all today. Their first rule would land
    // on line 1, where a rule with conditions is not allowed — and the × that
    // would strip its condition is switched off while a rule has only one, so
    // it could never be made valid. Seed the catch-all line with it.
    const base = list.length ? list : [{ name: null, when: [], then: { from_attributes: true } }];
    replace([...base, newRule()]);
    setOpenIndex(base.length);
  };
  const removeRule = i => {
    replace(list.filter((_, n) => n !== i));
    setOpenIndex(null);
  };
  const move = (i, delta) => {
    const j = i + delta;
    // The fallback is the floor. Moving a rule under it would make it
    // unreachable, since the fallback matches everything.
    if (i === 0 || j === 0 || j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    replace(next);
    setOpenIndex(j);
  };

  const doCopy = () => {
    if (!copyTarget || !onCopyTo) return;
    const target = onCopyTo(copyTarget);
    if (!target) return;
    const result = copyRulesTo(list, wcId, copyTarget, target, copyMode);
    target.apply(result.rules);
    setCopyNotes({
      wc: copyTarget,
      count: result.copiedCount,
      notes: result.notes,
    });
  };

  const exportRules = () => {
    const blob = new Blob(
      [JSON.stringify({ work_center: wcId, setup_time_rules: list }, null, 2)],
      { type: 'application/json' },
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${wcId}_setup_rules.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const importRules = event => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const incoming = parsed.setup_time_rules || parsed;
        if (!Array.isArray(incoming)) throw new Error('that file has no rule list in it');
        // Through the same copy path as machine-to-machine, so an imported rule
        // the machine cannot evaluate arrives switched off and SAID, rather than
        // looking fine and never firing.
        const result = copyRulesTo(
          incoming, parsed.work_center || wcId, wcId,
          { existing: list, knownFields, hasMsaBlock }, 'replace',
        );
        replace(result.rules);
        setCopyNotes({ wc: wcId, count: result.copiedCount, notes: result.notes });
      } catch (err) {
        setCopyNotes({ wc: wcId, count: 0, notes: [`Could not read that file: ${err.message}`] });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
        <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: TEAL }}>
          Setup Time Rules
        </h3>
        <button onClick={addRule} disabled={disabled} style={{
          padding: '5px 12px', fontSize: '11px', fontWeight: 600, color: 'white',
          backgroundColor: TEAL, border: 'none', borderRadius: '5px',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}>+ Add Rule</button>
        <div style={{ flex: 1 }} />
        <button onClick={exportRules} style={{ ...box, cursor: 'pointer' }}>Download</button>
        <button onClick={() => fileRef.current && fileRef.current.click()}
                disabled={disabled} style={{ ...box, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          Upload
        </button>
        <input ref={fileRef} type="file" accept="application/json"
               onChange={importRules} style={{ display: 'none' }} />
      </div>

      <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '10px', lineHeight: 1.5 }}>
        <b>If more than one rule fits the same job, the lower one applies.</b>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {list.map((rule, i) => {
          const isFallback = !((rule.when || []).length);
          const mine = issuesFor(i);
          const hasError = mine.some(x => x.level === 'error');
          const hasWarning = mine.some(x => x.level === 'warning');
          const borderColor = hasError ? RED : hasWarning ? AMBER
            : rule.enabled === false ? '#dfe6e9' : TEAL;
          const open = openIndex === i;

          return (
            <div key={i} style={{
              border: `2px solid ${borderColor}`, borderRadius: '6px',
              backgroundColor: rule.enabled === false ? '#fafafa' : 'white',
              opacity: rule.enabled === false ? 0.65 : 1,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px',
                cursor: isFallback ? 'default' : 'pointer',
              }} onClick={() => !isFallback && setOpenIndex(open ? null : i)}>
                <span style={{ fontSize: '11px', color: '#95a5a6', minWidth: '16px' }}>{i + 1}</span>

                {!isFallback && (
                  <input type="checkbox" checked={rule.enabled !== false} disabled={disabled}
                         onClick={e => e.stopPropagation()}
                         onChange={e => update(i, { enabled: e.target.checked })}
                         style={{ width: '15px', height: '15px' }} />
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Line 1 carries no text at all: it has no conditions, so
                      there is nothing to say about when it applies. Just the
                      number and what it charges. */}
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#2c3e50' }}>
                    {isFallback ? '' : (rule.name ? rule.name : describeRule(rule))}
                  </div>
                  {rule.name && !isFallback && (
                    <div style={{ fontSize: '11px', color: '#7f8c8d' }}>{describeRule(rule)}</div>
                  )}
                </div>

                <div style={{ fontSize: '11px', color: '#2c3e50', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {describeSource(rule)}
                </div>

                {!isFallback && (
                  <div style={{ display: 'flex', gap: '3px' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => move(i, -1)} disabled={disabled || i <= 1}
                            title="move up — lower priority" style={{ ...box, cursor: 'pointer' }}>↑</button>
                    <button onClick={() => move(i, 1)} disabled={disabled || i === list.length - 1}
                            title="move down — higher priority" style={{ ...box, cursor: 'pointer' }}>↓</button>
                    <button onClick={() => removeRule(i)} disabled={disabled}
                            title="delete" style={{ ...box, cursor: 'pointer', color: RED }}>×</button>
                  </div>
                )}
              </div>

              {mine.map((issue, n) => (
                <div key={n} style={{
                  fontSize: '11px', padding: '0 12px 8px 38px', lineHeight: 1.45,
                  color: issue.level === 'error' ? RED : AMBER,
                }}>
                  {issue.level === 'error' ? '✕ ' : '! '}{issue.message}
                </div>
              ))}

              {open && !isFallback && (
                <div style={{ borderTop: '1px solid #ecf0f1', padding: '12px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#7f8c8d' }}>
                    Name (optional)
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '4px 0 12px' }}>
                    <input type="text" disabled={disabled}
                           value={rule.name || ''}
                           onChange={e => update(i, { name: e.target.value.trim() || null })}
                           placeholder="leave blank to show the number instead"
                           style={{ ...box, flex: 1 }} />
                  </div>

                  {(rule.when || []).map((cond, ci) => (
                    <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', color: '#95a5a6', minWidth: '26px' }}>
                        {ci === 0 ? '' : 'and'}
                      </span>
                      <select value={cond.subject} disabled={disabled} style={box}
                              onChange={e => updateCondition(i, ci, e.target.value === 'position'
                                ? { subject: 'position', is: 'first_in_shift', field: undefined, op: undefined }
                                : { subject: e.target.value, field: cond.field || knownFields[0] || 'guideline', op: 'is', is: [] })}>
                        {SUBJECTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>

                      {cond.subject === 'position' ? (
                        <select value={cond.is} disabled={disabled} style={{ ...box, flex: 1 }}
                                onChange={e => updateCondition(i, ci, { is: e.target.value })}>
                          {positionChoices(cond.is).map(p => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <select value={cond.field || ''} disabled={disabled} style={box}
                                  onChange={e => updateCondition(i, ci, { field: e.target.value })}>
                            {(knownFields.length ? knownFields : [cond.field]).filter(Boolean)
                              .map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                          <select value={cond.op || 'is'} disabled={disabled} style={box}
                                  onChange={e => {
                                    const op = e.target.value;
                                    updateCondition(i, ci, op === 'same_as_this'
                                      ? { op, is: undefined, value: undefined }
                                      : { op, is: conditionValues(cond) });
                                  }}>
                            {OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          {cond.op !== 'same_as_this' && (
                            <ValueChips
                              values={conditionValues(cond)}
                              numeric={NUMERIC_OPS.includes(cond.op)}
                              suggestions={knownValues[cond.field] || []}
                              disabled={disabled}
                              onChange={vals => updateCondition(i, ci, NUMERIC_OPS.includes(cond.op)
                                ? { op: cond.op, value: vals[0], is: undefined }
                                : { is: vals, value: undefined })}
                            />
                          )}
                        </>
                      )}

                      <button disabled={disabled || (rule.when || []).length <= 1}
                              onClick={() => update(i, { when: rule.when.filter((_, n) => n !== ci) })}
                              style={{ ...box, cursor: 'pointer', color: RED }}>×</button>
                    </div>
                  ))}
                  <button disabled={disabled}
                          onClick={() => update(i, { when: [...(rule.when || []), newCondition()] })}
                          style={{ ...box, cursor: 'pointer', marginBottom: '12px' }}>
                    + add condition
                  </button>

                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#7f8c8d', marginBottom: '6px' }}>
                    THE SETUP TIME IS
                  </div>
                  {/* What "calculated from the rolls" actually computes. The option
                      said only that it used the rolls, which tells you nothing you
                      could check a number against. */}
                  {sourceKind(rule) === 'formula' && (
                    <div style={{
                      fontSize: '11px', lineHeight: 1.6, margin: '2px 0 10px 24px',
                      padding: '8px 10px', borderLeft: `3px solid ${TEAL}`,
                      backgroundColor: 'rgba(22,160,133,0.05)', color: '#2c3e50',
                    }}>
                      <b>How the roll setup is worked out</b>
                      <div style={{ marginTop: '4px' }}>
                        1. How many rolls the job needs — its quantity divided by the
                        yards on one roll of that item, rounded up.
                      </div>
                      <div>
                        2. The setup is <b>{msaSettings ? msaSettings.default_hours : '—'} h</b>{' '}
                        for the first roll, plus{' '}
                        <b>{msaSettings ? msaSettings.hours_per_extra_roll : '—'} h</b>{' '}
                        for every roll after it.
                      </div>
                      {msaSettings && (
                        <div style={{ marginTop: '4px', color: '#7f8c8d' }}>
                          So 1 roll = {msaSettings.default_hours} h, 2 rolls ={' '}
                          {(Number(msaSettings.default_hours) + Number(msaSettings.hours_per_extra_roll)).toFixed(1)} h,
                          5 rolls ={' '}
                          {(Number(msaSettings.default_hours) + 4 * Number(msaSettings.hours_per_extra_roll)).toFixed(1)} h.
                        </div>
                      )}
                      <div style={{ marginTop: '4px', color: '#7f8c8d' }}>
                        Only applies to jobs whose number starts with{' '}
                        <b>{msaSettings ? (msaSettings.job_prefix || 'MSA') : 'MSA'}</b>, and only when the
                        item is in the yards-per-roll list
                        {msaSettings && msaSettings.yards_per_roll
                          ? ` (${Object.keys(msaSettings.yards_per_roll).length} items today)`
                          : ''}. A job that is not in that list falls through to the
                        rule below.
                      </div>
                    </div>
                  )}

                  {SOURCES.map(src => (
                    <div key={src.value} style={{ marginBottom: '4px' }}>
                      <label style={{
                        display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px',
                      }}>
                        <input type="radio" name={`src-${i}`} disabled={disabled}
                               checked={sourceKind(rule) === src.value}
                               onChange={() => update(i, {
                                 then: {
                                   hours: { hours: Number(rule.then.hours) || 0 },
                                   from_input: { from_input: true },
                                   startup: { shift_startup: true, plus_from_input: true },
                                   formula: { formula: 'msa_rolls' },
                                   from_attributes: { from_attributes: true },
                                 }[src.value],
                               })} />
                        {/* "____ hours", with the box where the number goes */}
                        {src.value === 'hours' && (
                          <input type="number" min="0" step="0.1"
                                 disabled={disabled || sourceKind(rule) !== 'hours'}
                                 value={sourceKind(rule) === 'hours' ? rule.then.hours : ''}
                                 onChange={e => update(i, { then: { hours: Number(e.target.value) } })}
                                 style={{ ...box, width: '70px' }} />
                        )}
                        {src.label}
                        {src.value === 'startup' && sourceKind(rule) === 'startup' && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input type="checkbox" disabled={disabled}
                                   checked={!!rule.then.plus_from_input}
                                   onChange={e => update(i, {
                                     then: { shift_startup: true, plus_from_input: e.target.checked },
                                   })} />
                            plus the input file
                          </label>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {otherWorkCenters.length > 0 && (
        <div style={{
          marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #ecf0f1',
          display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#7f8c8d' }}>Copy these rules to</span>
          <select value={copyTarget} disabled={disabled} style={box}
                  onChange={e => { setCopyTarget(e.target.value); setCopyNotes(null); }}>
            <option value="">choose a machine…</option>
            {otherWorkCenters.map(wc => <option key={wc} value={wc}>{wc}</option>)}
          </select>
          <select value={copyMode} disabled={disabled} style={box}
                  onChange={e => setCopyMode(e.target.value)}>
            <option value="append">add to its rules</option>
            <option value="replace">replace its rules</option>
          </select>
          <button onClick={doCopy} disabled={disabled || !copyTarget}
                  style={{ ...box, cursor: 'pointer', fontWeight: 600 }}>Copy</button>
        </div>
      )}

      {copyNotes && (
        <div style={{
          marginTop: '8px', fontSize: '11px', lineHeight: 1.5, padding: '8px 10px',
          borderRadius: '5px', backgroundColor: 'rgba(22,160,133,0.06)',
          border: `1px solid ${copyNotes.notes.length ? AMBER : TEAL}`,
        }}>
          <b>{copyNotes.count} {copyNotes.count === 1 ? 'rule' : 'rules'} copied to {copyNotes.wc}.</b>
          {copyNotes.notes.map((n, i) => <div key={i} style={{ color: AMBER }}>{n}</div>)}
        </div>
      )}
    </div>
  );
}

/** One condition's values: chips, added from the loaded dispatch list or typed. */
function ValueChips({ values, numeric, suggestions, disabled, onChange }) {
  const [draft, setDraft] = useState('');

  if (numeric) {
    return (
      <input type="number" step="0.1" disabled={disabled} value={values[0] !== undefined ? values[0] : ''}
             onChange={e => onChange([e.target.value === '' ? '' : Number(e.target.value)])}
             style={{ ...box, width: '90px' }} />
    );
  }

  const add = v => {
    const trimmed = String(v).trim();
    if (!trimmed || values.some(x => String(x).toUpperCase() === trimmed.toUpperCase())) return;
    onChange([...values, trimmed]);
    setDraft('');
  };

  return (
    <div style={{
      flex: 1, display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap',
      ...box, padding: '3px 6px',
    }}>
      {values.map(v => (
        <span key={String(v)} style={{
          fontSize: '10.5px', fontWeight: 600, backgroundColor: '#eef3f8',
          border: '1px solid #dfe6e9', borderRadius: '3px', padding: '1px 4px',
        }}>
          {String(v)}
          <button disabled={disabled}
                  onClick={() => onChange(values.filter(x => x !== v))}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: RED, padding: '0 0 0 3px' }}>×</button>
        </span>
      ))}
      <input list={suggestions.length ? `vals-${suggestions.length}-${values.length}` : undefined}
             value={draft} disabled={disabled} placeholder="add a value…"
             onChange={e => setDraft(e.target.value)}
             onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(draft); } }}
             onBlur={() => draft && add(draft)}
             style={{ border: 'none', outline: 'none', fontSize: '11px', minWidth: '90px', flex: 1 }} />
      {suggestions.length > 0 && (
        <datalist id={`vals-${suggestions.length}-${values.length}`}>
          {suggestions.map(s => <option key={String(s)} value={String(s)} />)}
        </datalist>
      )}
    </div>
  );
}
